import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faPen,
  faImage,
  faBan,
  faSearch,
  faBoxOpen,
  faCircleExclamation,
  faXmark,
  faCheckCircle,
  faLink,
  faCopy,
  faVolumeHigh,
  faVolumeXmark
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import useFetch from "../../hooks/useFetch";
import { useHerKart } from "./hooks/useHerKart";
import WebpImgUploader from "../purpledoor/modules/components/WebpImgUploader";
import AILightComponent from "../../components/AILightComponent";

const emptyForm = {
  name: "",
  category: "",
  description: "",
  entrepreneur: "",
  store: "",
  storeCode: "",
  locality: "",
  city: "",
  mrp: "",
  sellingPrice: "",
  deliveryCharge: "",
  remarks: "",
  tts: "",
  unit: "",
  stock: "",
  images: [],
  imageLocation: "",
  featured: false,
  bestseller: false,
  pickup: false,
  localDelivery: false,
  cod: true,
  isBrand: false,
  isInnovation: false,
  isPilotSample: false,
  isActive: true,
};

const HerKartManageProducts = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const {
    fetchMyProducts,
    createProduct,
    updateProduct,
    deactivateProduct,
    fetchMyStores,
  } = useHerKart();

  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [uploaderForId, setUploaderForId] = useState(null); // product _id, or "new"
  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);
  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((prev) => !prev);

  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);

  const handleToggleSpeakTTS = () => {
    if (!("speechSynthesis" in window)) {
      setMessage({ type: "error", text: "Voice playback isn't supported in this browser." });
      return;
    }

    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(form.tts);
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);

    window.speechSynthesis.cancel(); // stop anything already playing
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  const [linkForId, setLinkForId] = useState(null);

  const [productId, setProductId] = useState(null);

  useEffect(() => {
    if (uploaderForId === "new" && !productId) {
      fetchData({ url: "/uploads/new-product-id", method: "get" }).then((res) => {
        if (res?.productId) setProductId(res.productId);
      });
    }
  }, [uploaderForId]);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [productsRes, storesRes] = await Promise.all([
        fetchMyProducts(),
        fetchMyStores(),
      ]);

      if (productsRes?.success) {
        setProducts(productsRes.products || []);
      }
      if (storesRes?.success) {
        setStores(storesRes.stores || []);
      }
    } catch (e) {
      console.error("Products fetch error:", e);
      setMessage({ type: "error", text: "Could not load your products." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (statusFilter !== "All") {
      list = list.filter((p) =>
        statusFilter === "Active" ? p.isActive : !p.isActive,
      );
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((p) =>
        [p.name, p.category, p.storeCode]
          .join(" ")
          .toLowerCase()
          .includes(keyword),
      );
    }

    return list;
  }, [products, search, statusFilter]);

  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setMessage(null);
  };

  const openEditForm = (product) => {
    setEditingId(product._id);
    setForm({
      name: product.name || "",
      category: product.category || "",
      description: product.description || "",
      entrepreneur: product.entrepreneur || "",
      store: product.store || "",
      storeCode: product.storeCode || "",
      locality: product.locality || "",
      city: product.city || "",
      mrp: product.mrp ?? "",
      sellingPrice: product.sellingPrice ?? "",
      deliveryCharge: product.deliveryCharge ?? "",
      remarks: product.remarks || "",
      tts: product.tts || "",
      unit: product.unit || "",
      stock: product.stock ?? "",
      images: product.images || [],
      imageLocation: product.imageLocation || "",
      featured: !!product.featured,
      bestseller: !!product.bestseller,
      pickup: !!product.pickup,
      localDelivery: !!product.localDelivery,
      cod: !!product.cod,
      isBrand: !!product.isBrand,
      isInnovation: !!product.isInnovation,
      isPilotSample: !!product.isPilotSample,
      isActive: !!product.isActive,
    });
    setShowForm(true);
    setMessage(null);
  };

  const closeForm = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsSpeakingTTS(false);
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setProductId(null);
    setEnableGenAIHelp(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "storeCode") {
      const selectedStore = stores.find((s) => s.storeCode === value);
      setForm((prev) => ({
        ...prev,
        storeCode: value,
        store: selectedStore?.storeName || "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!form.name.trim() || !form.sellingPrice) {
      setMessage({
        type: "error",
        text: "Product Name and Selling Price are required.",
      });
      return;
    }

    const payload = {
      ...form,
      mrp: form.mrp === "" ? 0 : Number(form.mrp),
      sellingPrice: Number(form.sellingPrice),
      deliveryCharge:
        form.deliveryCharge === "" ? 0 : Number(form.deliveryCharge),
      stock: form.stock === "" ? 0 : Number(form.stock),
    };

    setSaving(true);
    try {
      if (editingId) {
        const response = await updateProduct(editingId, payload);
        if (response?.success) {
          setProducts((prev) =>
            prev.map((p) => (p._id === editingId ? response.product : p)),
          );
          setMessage({
            type: "success",
            text: "Product updated successfully.",
          });
          closeForm();
        } else {
          setMessage({ type: "error", text: "Failed to update product." });
        }
      } else {
        const response = await createProduct({ ...payload, _id: productId });
        if (response?.success) {
          setProducts((prev) => [response.product, ...prev]);
          setMessage({
            type: "success",
            text: "Product created successfully.",
          });
          closeForm();
          setProductId(null);
        } else {
          setMessage({
            type: "error",
            text: response?.message || "Failed to create product.",
          });
        }
      }
    } catch (e) {
      console.error("Product save error:", e);
      setMessage({ type: "error", text: "Failed to save product." });
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async (id) => {
    try {
      const response = await deactivateProduct(id);
      if (response?.success) {
        setProducts((prev) =>
          prev.map((p) => (p._id === id ? response.product : p)),
        );
        setMessage({ type: "success", text: "Product deactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to deactivate product." });
      }
    } catch (e) {
      console.error("Deactivate error:", e);
      setMessage({ type: "error", text: "Failed to deactivate product." });
    } finally {
      setConfirmDeactivateId(null);
    }
  };

  const handleReactivate = async (id) => {
    try {
      const response = await updateProduct(id, { isActive: true });
      if (response?.success) {
        setProducts((prev) =>
          prev.map((p) => (p._id === id ? response.product : p)),
        );
        setMessage({ type: "success", text: "Product reactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to reactivate product." });
      }
    } catch (e) {
      console.error("Reactivate error:", e);
      setMessage({ type: "error", text: "Failed to reactivate product." });
    }
  };

  const handleImageChange = async (productId, filename, url) => {
    const nextImages = [url];

    if (productId === "new") {
      setForm((prev) => ({ ...prev, images: nextImages }));
      setUploaderForId(null);
      return;
    }

    try {
      const response = await updateProduct(productId, { images: nextImages });
      if (response?.success) {
        setProducts((prev) =>
          prev.map((p) => (p._id === productId ? response.product : p)),
        );
        setMessage({ type: "success", text: "Product image updated." });
      }
    } catch (e) {
      console.error("Image update error:", e);
      setMessage({ type: "error", text: "Failed to update product image." });
    } finally {
      setUploaderForId(null);
    }
  };

  const buildProductLink = (product) =>
    `https://www.think5whys.com/herkart/${product.productCode || product._id}`;

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setMessage({ type: "success", text: "Link copied to clipboard." });
    } catch (e) {
      console.error("Copy link error:", e);
      setMessage({ type: "error", text: "Could not copy link." });
    }
  };

  const handleShareOnWhatsApp = (product) => {
    const link = buildProductLink(product);
    const text = encodeURIComponent(
      `Check out ${product.name} on HerKart: ${link}`,
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <div className="flex flex-col justify-start">
              <h1 className="text-xl font-bold text-pink-300 text-left">
                My Products
              </h1>
              <p className="text-xs text-slate-200">
                Manage your FoundHer product catalogue
              </p>
            </div>
          </div>

          <button
            onClick={openAddForm}
            title="Add New Product"
            className="flex h-10 items-center gap-2 rounded-xl bg-pink-600 w-fit px-2 py-1 hover:bg-pink-500"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        {message && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success" ? faCheckCircle : faCircleExclamation
              }
            />
            {message.text}
          </div>
        )}

        {stores.length === 0 && !loading && (
          <div className="mb-4 flex flex-col lg:flex-row items-center gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
            <FontAwesomeIcon icon={faCircleExclamation} />
            You don't have any private stores yet.{" "}
            <Link to="/herkart/manage-stores" className="underline">
              Create one first
            </Link>{" "}
            so you can link your products to it and make your exclusive private
            online store with your own products..
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, category, or store code..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] w-fit px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <div className="mb-6 rounded-3xl border border-pink-500/30 bg-white/5 p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-pink-300">
                {editingId ? "Edit Product" : "Add New Product"}
              </h2>
              <button
                onClick={closeForm}
                className="text-slate-300 hover:text-white w-fit px-1 py-1"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section: Basic Info */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Basic Info
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={form.category}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm text-gray-200">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleFormChange}
                      style={{ height: "100px" }}
                      maxLength={1000}
                      className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Entrepreneur Name
                    </label>
                    <input
                      type="text"
                      name="entrepreneur"
                      value={form.entrepreneur}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Store
                    </label>
                    <select
                      name="storeCode"
                      value={form.storeCode}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
                    >
                      <option value="">— Select Store —</option>
                      {stores.map((s) => (
                        <option key={s._id} value={s.storeCode}>
                          {s.storeName} ({s.storeCode})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Locality
                    </label>
                    <input
                      type="text"
                      name="locality"
                      value={form.locality}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Pricing & Stock */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Pricing & Stock
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      MRP
                    </label>
                    <input
                      type="number"
                      name="mrp"
                      value={form.mrp}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Selling Price *
                    </label>
                    <input
                      type="number"
                      name="sellingPrice"
                      value={form.sellingPrice}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Unit
                    </label>
                    <input
                      type="text"
                      name="unit"
                      value={form.unit}
                      onChange={handleFormChange}
                      placeholder="e.g. 1 piece, 500g"
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Stock
                    </label>
                    <input
                      type="number"
                      name="stock"
                      value={form.stock}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-200">
                      Delivery Charge (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="deliveryCharge"
                      value={form.deliveryCharge}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm text-gray-200">
                      Remarks (shown at checkout)
                    </label>
                    <input
                      type="text"
                      name="remarks"
                      value={form.remarks}
                      onChange={handleFormChange}
                      className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Flags & Tags */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Flags & Tags
                </h3>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={form.featured}
                      onChange={handleFormChange}
                    />
                    Featured
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="bestseller"
                      checked={form.bestseller}
                      onChange={handleFormChange}
                    />
                    Bestseller
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="pickup"
                      checked={form.pickup}
                      onChange={handleFormChange}
                    />
                    Pickup Available
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="localDelivery"
                      checked={form.localDelivery}
                      onChange={handleFormChange}
                    />
                    Local Delivery
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="cod"
                      checked={form.cod}
                      onChange={handleFormChange}
                    />
                    COD Available
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="isBrand"
                      checked={form.isBrand}
                      onChange={handleFormChange}
                    />
                    Is Brand
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="isInnovation"
                      checked={form.isInnovation}
                      onChange={handleFormChange}
                    />
                    Is Innovation
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="isPilotSample"
                      checked={form.isPilotSample}
                      onChange={handleFormChange}
                    />
                    Is Pilot Sample
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleFormChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              {/* Section: Media */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Media
                </h3>

                {form.images?.[0] && !uploaderForId && (
                  <img
                    src={form.images[0]}
                    alt=""
                    className="mb-2 h-24 w-24 rounded-xl object-cover"
                  />
                )}

                {uploaderForId === "new" && productId ? (
                  // <ImageUploader
                  //   label="Add/Edit Product Image"
                  //   value={form.images?.[0] || ""}
                  //   token={authState?.token}
                  //   uploadUrl="/upload-herkartproductimage"
                  //   extraHeaders={{ "x-product-id": productId }}
                  //   onChange={(filename) => handleImageChange("new", filename)}
                  //   onClose={() => setUploaderForId(null)}
                  // />
                  <WebpImgUploader
                    label="Add/Edit Product Image"
                    value={form.images?.[0] || ""}
                    token={authState?.token}
                    uploadUrl="/upload-herkartproductimage"
                    extraHeaders={{ "x-product-id": productId }}
                    onChange={(filename, url) =>
                      handleImageChange("new", filename, url)
                    }
                    onClose={() => setUploaderForId(null)}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setUploaderForId("new")}
                    className="rounded-xl bg-white/10 w-fit px-2 py-1 text-sm hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faImage} className="mr-2" />
                    {form.images?.[0] ? "Change Image" : "Upload Image"}
                  </button>
                )}
              </div>

              {/* Section: Voice (TTS) */}
              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Voice Script (Text-to-Speech)
                </h3>

                <label className="mb-1 block text-sm text-gray-200">
                  TTS Script (max 500 characters)
                </label>
                <textarea
                  name="tts"
                  value={form.tts}
                  onChange={handleFormChange}
                  maxLength={500}
                  style={{ height: "100px" }}
                  placeholder="A short spoken script that will be read aloud to customers browsing this product..."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-slate-400">
                    {form.tts.length}/500 characters
                  </p>

                  {form.tts.trim() && (
                    <button
                      type="button"
                      onClick={handleToggleSpeakTTS}
                      title={isSpeakingTTS ? "Stop" : "Play TTS Script"}
                      className={`flex h-8 w-8 items-center justify-center rounded-full transition-all ${
                        isSpeakingTTS
                          ? "bg-pink-500 text-white"
                          : "bg-white/10 text-pink-300 hover:bg-white/20"
                      }`}
                    >
                      <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
                    </button>
                  )}
                </div>

                <div className="mt-3 flex flex-col w-full gap-2">
                  <button
                    type="button"
                    onClick={toggleGenAIHelp}
                    className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
                      enableGenAIHelp
                        ? "bg-purple-500 text-white"
                        : "bg-[#111827] text-purple-300 border border-purple-500/30"
                    }`}
                  >
                    {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
                  </button>

                  {enableGenAIHelp && (
                    <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                      <AILightComponent
                        mode="herkart_product_tts_script"
                        param1={"🗣️ AI Voice Script Generator"}
                        param2={`
You are an expert Voice Commerce Copywriter creating a Text-to-Speech (TTS) script for a browser voice assistant that will read this product aloud to a shopper on an e-commerce platform called HerKart.

Product Details:
Name: ${form.name || "N/A"}
Category: ${form.category || "N/A"}
Description: ${form.description || "N/A"}
Entrepreneur/Brand: ${form.entrepreneur || "N/A"}
MRP: ${form.mrp || "N/A"} rupees
Selling Price: ${form.sellingPrice || "N/A"} rupees
Unit: ${form.unit || "N/A"}
Locality/City: ${form.locality || ""} ${form.city || ""}
Remarks: ${form.remarks || "N/A"}

Your task:
Write ONE natural-sounding spoken script that a browser Text-to-Speech engine (like the Web Speech API) will read aloud to a customer browsing this product.

STRICT RULES:
1. Output ONLY the final spoken script text. No headings, no labels, no emojis, no markdown, no quotation marks, no bullet points.
2. Maximum 500 characters INCLUDING spaces and punctuation. Count carefully and stay under the limit.
3. Use short, simple sentences that sound natural when spoken aloud (avoid long compound sentences and abbreviations; spell out symbols like rupees or percent instead of using ₹ or %).
4. Mention the product name, what makes it special, who made it if a local entrepreneur is named, and the price, in a friendly, conversational, sales-encouraging tone.
5. Do NOT invent facts that are not present in the details above. If information is missing, skip it gracefully.
6. End with a short, warm call to action, such as "Order yours today".
7. Do not use emojis, asterisks, hashtags, or any characters a TTS engine may mispronounce.

Return only the final script text, ready to paste directly into the TTS field.
`}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500 disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Update Product"
                      : "Create Product"}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="w-fit rounded-xl border border-white/10 px-2 py-1 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-gray-200">
            Loading your products...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon
              icon={faBoxOpen}
              className="mx-auto mb-4 text-4xl text-slate-300"
            />
            <h3 className="text-xl font-semibold text-gray-300">
              No products yet
            </h3>
            <p className="mt-2 text-sm text-gray-200">
              {products.length === 0
                ? "Add your first product to get started."
                : "No products match your search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts
              .filter((product) => product._id !== editingId)
              .map((product) => (
                <div
                  key={product._id}
                  className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 md:flex-row md:items-center"
                >
                  <img
                    src={
                      product.images?.[0] ||
                      "https://placehold.co/100x100/18181b/f9a8d4?text=Product"
                    }
                    alt={product.name}
                    className="h-16 w-16 rounded-xl object-cover"
                  />

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-cyan-200">
                        {product.name}
                      </h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          product.isActive
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-red-500/20 text-red-300"
                        }`}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200">
                      {product.category}{" "}
                      {product.storeCode ? `• Store: ${product.storeCode}` : ""}
                    </p>
                    <p className="text-sm text-gray-200">
                      ₹{product.sellingPrice} · Stock: {product.stock}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditForm(product)}
                      title="Edit"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>

                    {/* <button
                      onClick={() =>
                        setUploaderForId(
                          uploaderForId === product._id ? null : product._id,
                        )
                      }
                      title="Change Image"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </button> */}
                    <button
                      onClick={() => setUploaderForId(uploaderForId === product._id ? null : product._id)}
                      title="Change Image"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faImage} />
                    </button>

                    <button
                      onClick={() => setLinkForId(linkForId === product._id ? null : product._id)}
                      title="Create Product Link"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faLink} />
                    </button>

                    {product.isActive ? (
                      <button
                        onClick={() => setConfirmDeactivateId(product._id)}
                        title="Deactivate"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-300 hover:bg-red-600/30"
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivate(product._id)}
                        title="Reactivate"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </button>
                    )}
                  </div>

                  {uploaderForId === product._id && (
                    <div className="w-full md:w-auto">
                      <WebpImgUploader
                      label="Product Image"
                      value={product.images?.[0]}
                      token={authState?.token}
                      uploadUrl="/upload-herkartproductimage"
                      extraHeaders={{ "x-product-id": product._id }}
                      onChange={(filename, url) => handleImageChange(product._id, filename, url)}
                      onClose={() => setUploaderForId(null)}
                    />
                  </div>
                )}

                {linkForId === product._id && (
                  <div className="w-full mt-2 rounded-xl border border-white/10 bg-[#0b1120] p-3 flex flex-col md:flex-row md:items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={buildProductLink(product)}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 rounded-lg border border-white/10 bg-transparent px-3 py-2 text-sm text-slate-200 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopyLink(buildProductLink(product))}
                        title="Copy Link"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        onClick={() => handleShareOnWhatsApp(product)}
                        title="Share on WhatsApp"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deactivate confirmation */}
      {confirmDeactivateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0b1120] p-6">
            <h3 className="text-lg font-bold">Deactivate this product?</h3>
            <p className="mt-2 text-sm text-gray-200">
              This product will no longer be visible to customers. You can't
              undo this from here.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => confirmDeactivate(confirmDeactivateId)}
                className="flex-1 rounded-xl bg-red-600 w-fit px-2 py-1 font-semibold hover:bg-red-500"
              >
                Yes, Deactivate
              </button>
              <button
                onClick={() => setConfirmDeactivateId(null)}
                className="flex-1 rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartManageProducts;
